/**
 * El puente desde lo que ya existe.
 *
 * Las quince secciones de reportes ya arman `rows` + `columns` para pintar su
 * tabla y para exportar. Esto convierte ese par en un documento, para que todas
 * ganen encabezado, resumen y pie sin tocarlas una por una.
 *
 * Es un mínimo honesto, no un reporte a medida: a partir de una tabla no se
 * puede inventar un resumen de negocio. Lo que sí se sabe —cuántas filas, qué
 * periodo, qué unidad— se enseña, y las secciones que quieran más le pasan sus
 * propios bloques por `extra`.
 */

import type { ReportTableColumn } from "../components/ReportPointsTable";
import { compactar } from "./tipos";
import type { Bloque, Celda, ColumnaDoc, DocumentoReporte } from "./tipos";

export interface MetaDocumento {
  titulo: string;
  periodo: string;
  archivo: string;
  /** Cliente y unidad, para la línea del encabezado. */
  subtitulo?: string;
  sello?: string;
}

/**
 * ¿Esta columna es numérica?
 *
 * Se decide con los DATOS, no con el nombre: una columna llamada «Velocidad»
 * que trae «sin dato» no se alinea a la derecha. Se miran las primeras filas
 * porque recorrer ochenta mil para decidir una alineación no se paga.
 */
function esNumerica<T>(filas: T[], clave: keyof T): boolean {
  const muestra = filas.slice(0, 25).map((f) => f[clave]).filter((v) => v !== null && v !== undefined && v !== "");
  if (muestra.length === 0) return false;
  return muestra.every((v) => {
    if (typeof v === "number") return true;
    const s = String(v).replace(/[\s,.%°]/g, "").replace(/^[-+]/, "");
    return s !== "" && /^[\d]+$/.test(s);
  });
}

export function columnasDeTabla<T extends { id: string }>(
  filas: T[],
  columnas: ReportTableColumn<T>[],
): ColumnaDoc[] {
  return columnas.map((c) => ({
    clave: String(c.key),
    titulo: c.header,
    alinea:
      c.align === "right" || (!c.align && esNumerica(filas, c.key))
        ? "derecha"
        : c.align === "center"
          ? "centro"
          : "izquierda",
  }));
}

export function filasDeTabla<T extends { id: string }>(
  filas: T[],
  columnas: ReportTableColumn<T>[],
): Record<string, Celda>[] {
  return filas.map((f) => {
    const out: Record<string, Celda> = {};
    for (const c of columnas) out[String(c.key)] = f[c.key] as Celda;
    return out;
  });
}

/**
 * Tope de filas que viajan al PDF.
 *
 * Un histórico de un mes son decenas de miles de posiciones. Impresas dan un
 * documento de cientos de páginas que nadie abre y que tarda minutos en
 * generarse en el navegador. El PDF es para revisar y archivar; el detalle
 * completo es justo para lo que sirve el Excel, y el pie de la tabla lo dice.
 */
export const TOPE_FILAS_PDF = 1200;

export function documentoDesdeTabla<T extends { id: string }>(
  filas: T[],
  columnas: ReportTableColumn<T>[],
  meta: MetaDocumento,
  extra: (Bloque | null | false)[] = [],
): DocumentoReporte {
  const cols = columnasDeTabla(filas, columnas);
  return {
    titulo: meta.titulo,
    folio: meta.periodo,
    subtitulo: meta.subtitulo ?? "",
    sello: meta.sello,
    archivo: meta.archivo,
    bloques: compactar([
      {
        tipo: "resumen",
        datos: [
          { etiqueta: "Registros", valor: filas.length.toLocaleString("es-MX") },
          { etiqueta: "Columnas", valor: String(cols.length) },
          { etiqueta: "Periodo", valor: meta.periodo },
        ],
      },
      ...extra,
      {
        tipo: "tabla",
        titulo: "Detalle",
        columnas: cols,
        filas: filasDeTabla(filas, columnas),
        topePdf: TOPE_FILAS_PDF,
      },
    ]),
  };
}
